import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useShareInfographic } from "../../domains/infographic/hooks/useShareInfographic";
import type { Api } from "../../services/api";
import { thumbnailUrl } from "../../../../api/src/shared/domain/video-url";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemInfographic } from "../../tools/analytics/domains/infographic.analytics";
import { useCongressAttribution } from "../../domains/congress/congressAttribution";
import { ContentPublicationCard } from "../ui/ContentPublicationCard";
import { journalSource, type PublicationSource } from "../ui/SourceLabel";
import type { PublicationCardRecommendation } from "../ui/PublicationCard";
import type { ForYouFeedItem } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";

type InfographicListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Infographic" }
>;
type FeedInfographicItem = Extract<
  ForYouFeedItem,
  { content_format: "Infographic" }
>;

type InfographicCardData = InfographicListItem | FeedInfographicItem;

function formatDate(value: Date | string | null, locale: string): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** The journal it appeared in, else the congress it was shown at. */
function resolveSource(
  infographic: InfographicCardData
): PublicationSource | null {
  return journalSource(
    infographic.journal === null ? infographic.congress : infographic.journal
  );
}

export default function InfographicCard({
  roomId,
  infographic,
  analyticsListName,
  recommendation,
  onClick,
  className,
}: {
  roomId: string | null;
  infographic: InfographicCardData;
  analyticsListName: AnalyticsItemList;
  recommendation: PublicationCardRecommendation | null;
  onClick: (() => void) | null;
  className: string | null;
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const { infographicLink } = usePageLinks(roomId);
  const congressAttribution = useCongressAttribution(analyticsListName);
  const { currentUser } = useCurrentUser();
  const { triggerShare } = useShareInfographic(analyticsListName);

  const source = resolveSource(infographic);
  const imageUrl = thumbnailUrl(infographic);
  const articleCount = infographic.associatedArticles.length;
  const detail =
    articleCount > 0
      ? t("based on {{count}} articles", { count: articleCount })
      : null;

  return (
    <ContentPublicationCard
      contentType="infographic"
      title={infographic.title}
      source={source}
      date={formatDate(infographic.publication_date, activeLang)}
      meta={t("Infographic")}
      metaVariant="lemon"
      detail={detail}
      imageUrl={imageUrl}
      recommendation={recommendation}
      isBookmarked={false}
      onBookmark={currentUser === null ? null : null}
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(infographic, currentUser, roomId);
            }
      }
      onClick={() => {
        if (onClick !== null) onClick();
        gaEventSelectItemInfographic({
          infographic,
          listName: analyticsListName,
        });
        // Attribute the open to a congress space when this card is drawing
        // one. Gated on `analyticsListName` like the article and video cards:
        // this one renders no PlaylistPanel today, but the gate is what makes
        // the rule the same across the family instead of a property of where
        // each card happens to sit. The destination page reads this back and
        // attaches it to the `view_item` it already fires.
        history.push({
          pathname: infographicLink(infographic),
          state: { congressAttribution },
        });
      }}
      className={className === null ? undefined : className}
    />
  );
}
